/**
 * canvas-engine/presets/decompositionAdapters/flexOffice.js
 *
 * FlexOffice (workspace booking) family adapter. FlexOffice sections decompose
 * faithfully with the core engine's vertical-stack layout, so most delegate
 * straight to `buildSectionCanvas`.
 *
 * Family tweak: two-column sections that carry a single foreground image (the
 * about + service detail shells) read best with the image beside the lead copy
 * — the shared split hook handles that. The multi-image hero falls through to
 * an editable image grid in the default stack.
 *
 * The adapter accepts EITHER a transcribed `FLEX_OFFICE_PRESETS` descriptor OR
 * a section-type string looked up in `FLEX_OFFICE_PRESETS`, laying out at the
 * fixed `Reference_Width` with `idPrefix = sectionType`.
 *
 * AUTH-VARIANT PASSTHROUGH (Req 3.5): this adapter forwards caller decompose
 * options verbatim — `adapterOptions(sectionType, REFERENCE_FRAME_WIDTH,
 * options)` spreads `...options` into the `buildSectionCanvas` opts — so an
 * `options.authVariant` (e.g. `decomposeSection('flexOfficeAuth', { authVariant:
 * 'signUp' })`) threads straight through `buildFamilyCanvas` to
 * `buildSectionCanvas`, where `content.variants[authVariant]` is merged over
 * `content` before layout. That lets `flexOfficeAuth` decompose its
 * `content.variants.signUp` block for the sign-up page (the default `signIn`
 * copy is used when no variant is requested), while the variant block is always
 * preserved losslessly under `canvas.source.preserved.content`. No FlexOffice-
 * specific handling is needed here; the passthrough is additive and behavior is
 * unchanged when `authVariant` is absent.
 *
 * TYPE COVERAGE: `FLEX_OFFICE_TYPES` auto-derives every handled sectionType from
 * `FLEX_OFFICE_PRESETS` via `handledTypesOf`, so the transcribed flow-page and
 * auth presets (incl. `flexOfficeAuth` and all `flexOffice*` flow pages) are
 * covered automatically — no manual list edits are required when descriptors
 * are added to `flexOfficePresets.js`.
 *
 * ZERO-REUSE CONSTRAINT: imports ONLY sibling engine modules + transcribed
 * presetData VALUES. Never emits a `native` element.
 *
 * Requirements: 4.1, 5.1, 5.4, 5.6, 5.7, 3.1, 3.4, 3.5
 */

import { REFERENCE_FRAME_WIDTH } from './buildSectionCanvas';
import { buildFamilyCanvas, wantsSplit } from './familyLayout';
import { resolvePreset, adapterOptions, handledTypesOf } from './resolvePreset';
import { FLEX_OFFICE_PRESETS } from '../presetData/flexOfficePresets';

/** The FlexOffice section types this adapter handles (keys + sectionTypes). */
export const FLEX_OFFICE_TYPES = Object.freeze(handledTypesOf(FLEX_OFFICE_PRESETS));

/** True when `sectionType` is decomposed by the FlexOffice adapter. */
export function handlesFlexOfficeType(sectionType) {
  return FLEX_OFFICE_TYPES.includes(typeof sectionType === 'string' ? sectionType.trim() : sectionType);
}

/**
 * Decompose a FlexOffice section into a normalized Canvas_Model.
 *
 * @param {(string|object)} sectionTypeOrData a `FLEX_OFFICE_PRESETS` key /
 *   sectionType, or a transcribed FlexOffice preset DATA descriptor
 * @param {object} [options] `buildSectionCanvas` options; forwarded verbatim.
 *   `options.authVariant` (e.g. `'signUp'`) selects `content.variants[authVariant]`
 *   for `flexOfficeAuth` — merged over `content` in `buildSectionCanvas`.
 * @returns {object} a normalized Canvas_Model
 */
export function flexOfficeToCanvas(sectionTypeOrData, options = {}) {
  const { data, sectionType } = resolvePreset(sectionTypeOrData, FLEX_OFFICE_PRESETS, options);
  const opts = adapterOptions(sectionType, REFERENCE_FRAME_WIDTH, options);
  return buildFamilyCanvas(data, opts, { split: wantsSplit(data) });
}

export default flexOfficeToCanvas;
